/**
 * Linguistic Variation Analysis — Detects "template collapse" in question formulations.
 *
 * Identifies when AI-generated questions use repetitive language patterns:
 * - All questions start with the same word ("Bereken...", "Tel...")
 * - All questions use the same sentence structure
 * - No variation in question forms (all imperatives, no questions)
 * - No contextual variation (all bare calculations)
 *
 * Used by supportStage.ts for family-level validation.
 */

export interface LinguisticVariationAnalysis {
  totalQuestions: number
  firstWords: Record<string, number> // Frequency of first words
  questionMarks: number // % questions with question mark
  imperatives: number // % questions with imperative form
  contexts: number // % questions with context story (>100 chars)
  averageLength: number // Average question length in characters
}

/**
 * Extract the first significant word from a question.
 * Strips punctuation and converts to lowercase.
 */
function extractFirstWord(text: string): string {
  const trimmed = text.trim()
  // Remove leading punctuation/quotes
  const cleaned = trimmed.replace(/^["""''„«»\s]+/, '')
  // Get first word (split on whitespace)
  const firstWord = cleaned.split(/\s+/)[0]
  // Remove trailing punctuation
  return firstWord.replace(/[.,!?;:]+$/, '').toLowerCase()
}

/**
 * Check if a question uses imperative form (gebiedende wijs).
 * Common Dutch imperatives in educational context:
 * - bereken, tel, rond, vul, kies, zoek, schrijf, maak, geef, bepaal
 */
function isImperative(text: string): boolean {
  const IMPERATIVE_PATTERNS = [
    /^bereken\b/i, // "Bereken 3 + 4"
    /^tel\b/i, // "Tel op: 3 + 4"
    /^rond\b/i, // "Rond af op..."
    /^vul\b/i, // "Vul in..."
    /^kies\b/i, // "Kies het juiste antwoord"
    /^zoek\b/i, // "Zoek de oplossing"
    /^schrijf\b/i, // "Schrijf het antwoord"
    /^maak\b/i, // "Maak de som"
    /^geef\b/i, // "Geef het antwoord"
    /^bepaal\b/i, // "Bepaal de waarde"
    /^teken\b/i, // "Teken de lijn"
    /^meet\b/i, // "Meet de hoek"
    /^lees\b/i, // "Lees de tekst"
    /^vergelijk\b/i, // "Vergelijk de getallen"
  ]

  return IMPERATIVE_PATTERNS.some((pattern) => pattern.test(text.trim()))
}

/**
 * Check if a question has a question mark (vragende vorm).
 */
function hasQuestionMark(text: string): boolean {
  return text.includes('?')
}

/**
 * Check if a question has a context story (>100 chars suggests narrative).
 */
function hasContext(text: string): boolean {
  return text.length > 100
}

/**
 * Analyze linguistic variation across all questions in a family.
 * Returns metrics about "template collapse" (repetitive formulations).
 */
export function analyzeLinguisticVariation(
  questions: Array<{ question: string }>,
): LinguisticVariationAnalysis {
  const firstWords: Record<string, number> = {}
  let questionMarkCount = 0
  let imperativeCount = 0
  let contextCount = 0
  let totalLength = 0

  for (const q of questions) {
    const text = q.question ?? ''
    if (!text.trim()) continue

    // Track first word frequency
    const firstWord = extractFirstWord(text)
    if (firstWord) {
      firstWords[firstWord] = (firstWords[firstWord] || 0) + 1
    }

    // Track question forms
    if (hasQuestionMark(text)) questionMarkCount++
    if (isImperative(text)) imperativeCount++
    if (hasContext(text)) contextCount++
    totalLength += text.length
  }

  const total = questions.length

  return {
    totalQuestions: total,
    firstWords,
    questionMarks: total > 0 ? questionMarkCount / total : 0,
    imperatives: total > 0 ? imperativeCount / total : 0,
    contexts: total > 0 ? contextCount / total : 0,
    averageLength: total > 0 ? totalLength / total : 0,
  }
}

/**
 * Determine if a family shows signs of "template collapse" (repetitive formulations).
 * Returns a severity score (0-100) and a description of the issue.
 */
export function detectLinguisticTemplateCollapse(
  analysis: LinguisticVariationAnalysis,
): { severity: number; issue: string | null } {
  if (analysis.totalQuestions < 5) {
    return { severity: 0, issue: null } // Too few questions to judge
  }

  // Find most common first word
  const firstWordEntries = Object.entries(analysis.firstWords)
  if (firstWordEntries.length === 0) {
    return { severity: 0, issue: null }
  }

  const [mostCommonWord, frequency] = firstWordEntries.reduce((max, entry) =>
    entry[1] > max[1] ? entry : max,
  )

  const firstWordPct = frequency / analysis.totalQuestions

  // High severity: ≥70% start with same word
  if (firstWordPct >= 0.7) {
    return {
      severity: 30,
      issue: `${(firstWordPct * 100).toFixed(0)}% of questions start with "${mostCommonWord}"`,
    }
  }

  // Medium severity: ≥80% use same question form (all imperative or all questions)
  if (analysis.imperatives >= 0.8) {
    return {
      severity: 25,
      issue: `${(analysis.imperatives * 100).toFixed(0)}% of questions use imperative form — no variation in question style`,
    }
  }

  if (analysis.questionMarks >= 0.8) {
    return {
      severity: 20,
      issue: `${(analysis.questionMarks * 100).toFixed(0)}% of questions use question marks — consider adding imperative forms for variety`,
    }
  }

  // Low severity: No context variation (all bare calculations)
  if (analysis.contexts === 0 && analysis.totalQuestions >= 5) {
    return {
      severity: 15,
      issue: 'No questions include context stories — all are bare calculations',
    }
  }

  return { severity: 0, issue: null }
}

/**
 * Get suggestions for improving linguistic variation based on analysis.
 */
export function getLinguisticVariationSuggestions(
  analysis: LinguisticVariationAnalysis,
): string[] {
  const suggestions: string[] = []

  // Find most common first word
  const firstWordEntries = Object.entries(analysis.firstWords)
  if (firstWordEntries.length > 0) {
    const [mostCommonWord, frequency] = firstWordEntries.reduce((max, entry) =>
      entry[1] > max[1] ? entry : max,
    )
    const firstWordPct = frequency / analysis.totalQuestions

    if (firstWordPct >= 0.7) {
      suggestions.push(
        `Vary sentence starters: instead of always "${mostCommonWord}", try "Hoeveel is...", "Wat is...", "Als je... optelt..."`,
      )
    }
  }

  if (analysis.imperatives >= 0.8) {
    suggestions.push(
      'Add question forms: mix imperatives ("Bereken...") with questions ("Hoeveel is...?", "Wat krijg je als...?")',
    )
  }

  if (analysis.questionMarks >= 0.8) {
    suggestions.push(
      'Add imperative forms: mix questions ("Hoeveel...?") with commands ("Bereken...", "Tel op...")',
    )
  }

  if (analysis.contexts === 0 && analysis.totalQuestions >= 5) {
    suggestions.push(
      'Add context stories: include real-world scenarios (shopping, sports, travel) to make questions more engaging',
    )
  }

  if (suggestions.length === 0) {
    suggestions.push(
      'Continue varying question formulations to maintain engagement',
    )
  }

  return suggestions
}
